/**
 * Home's rules (TMMAPP-0152 §A), each written once and tested on its own:
 *
 * - **Mood and intent** stay in the browser (`sessionStorage`, per visit) and are never sent. An
 *   intent is stored only when the person picks one; until then it is the scope's default (Support
 *   me for Just me, Help us connect in a space), so a scope change moves an unpicked intent along.
 * - **The greeting's part of the day** comes from the hour in the person's `/me` zone.
 * - **Bills coming up** is a COUNT of the outgoing planned occurrences in the next 7 days, never a
 *   money total (summing rows is client recomputation, MM-D-53).
 * - **The focus goal** is the newest open GOAL with an envelope, in scope.
 * - **The nudge** is a fixed rule list, never AI text: the first rule that holds gives it.
 * - **Not now** hides the nudge until tomorrow (`localStorage`, the civil date).
 */
import type { MeetingCalendarEntry, MeetingItem } from '@/api/meetings';
import type { Occurrence } from '@/api/planned';
import type { CashflowPlan } from '@/api/projections';
import { addCivilDays } from '@/shared/servedWindow';
import { utcToZonedCivil } from '@/i18n/zonedCivil';
import { MOOD_FACES, type Mood } from '@/theme/categorical';
import type { HomeScope } from './homeScope';

export const MOOD_STORAGE_KEY = 'tmm_home_mood';
export const INTENT_STORAGE_KEY = 'tmm_home_intent';
export const NUDGE_STORAGE_KEY = 'tmm_nudge_dismissed_on';

export const INTENTS = ['support', 'understand', 'done', 'connect'] as const;
export type Intent = (typeof INTENTS)[number];

/** The intent each mood's note offers to switch to (the prototype's MOODS table, P:841). */
export const MOOD_INTENT: Record<Mood, Intent> = {
  stressed: 'support',
  overwhelmed: 'support',
  okay: 'understand',
  good: 'done',
  confident: 'connect',
};

export function isMood(value: unknown): value is Mood {
  return MOOD_FACES.some((face) => face.mood === value);
}

export function isIntent(value: unknown): value is Intent {
  return (INTENTS as readonly unknown[]).includes(value);
}

/** Support me for Just me; Help us connect in a space. */
export function defaultIntent(scope: HomeScope): Intent {
  return scope.kind === 'space' ? 'connect' : 'support';
}

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* sessionStorage unavailable: the pick holds until the page is left */
  }
}

export function readStoredMood(): Mood | null {
  const value = readSession(MOOD_STORAGE_KEY);
  return isMood(value) ? value : null;
}

export function writeStoredMood(mood: Mood): void {
  writeSession(MOOD_STORAGE_KEY, mood);
}

export function readStoredIntent(): Intent | null {
  const value = readSession(INTENT_STORAGE_KEY);
  return isIntent(value) ? value : null;
}

export function writeStoredIntent(intent: Intent): void {
  writeSession(INTENT_STORAGE_KEY, intent);
}

/** Not now was pressed today (the civil date in the person's zone). */
export function nudgeDismissedToday(today: string): boolean {
  try {
    return localStorage.getItem(NUDGE_STORAGE_KEY) === today;
  } catch {
    return false;
  }
}

export function dismissNudge(today: string): void {
  try {
    localStorage.setItem(NUDGE_STORAGE_KEY, today);
  } catch {
    /* localStorage unavailable: it stays hidden for this visit only */
  }
}

export type DayPart = 'morning' | 'afternoon' | 'evening';

/** Morning before noon, afternoon until 6 PM, evening after, by the hour in `zone`. */
export function dayPartAt(now: Date, zone: string): DayPart {
  const hour = Number(utcToZonedCivil(now.toISOString(), zone).time.slice(0, 2));
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  return 'evening';
}

/** The 7 days Bills coming up counts: today and the six after it. */
export function billsWindow(today: string): { from: string; to: string } {
  return { from: today, to: addCivilDays(today, 6) };
}

const inWindow = (date: string, window: { from: string; to: string }) =>
  date >= window.from && date <= window.to;

/** Just me: the outgoing occurrences still planned in the window (`GET /v1/me/planned`). */
export function billsCountOfOccurrences(
  occurrences: readonly Occurrence[],
  window: { from: string; to: string },
): number {
  return occurrences.filter(
    (row) => row.state === 'PLANNED' && row.amountMinor < 0 && inWindow(row.occursOn, window),
  ).length;
}

/**
 * A space: the outgoing planned events of its cash flow plan in the window — series occurrences and
 * plan lines; a slice is part of its occurrence, and an overdue item is past, not coming up.
 */
export function billsCountOfPlan(plan: CashflowPlan, window: { from: string; to: string }): number {
  return plan.events.filter((event) => outgoingAhead(event) && inWindow(event.occursOn, window))
    .length;
}

const outgoingAhead = (event: CashflowPlan['events'][number]) =>
  (event.kind === 'OCCURRENCE' || event.kind === 'PLANNED_EXPENSE') &&
  event.amountMinor < 0 &&
  (event.state === null || event.state === 'PLANNED') &&
  !event.overdue;

/**
 * A space's next outgoing plan event (a series occurrence or a plan line, still planned, not
 * overdue), soonest first; null when its plan has none. Home's bill tiles in a space read it, never
 * the person's own summary (MM-D-43; TMMAPP-0154 item 28).
 */
export function nextOutgoingOfPlan(
  plan: CashflowPlan,
): { occursOn: string; payee: string | null } | null {
  const ahead = plan.events
    .filter(outgoingAhead)
    .sort((a, b) => a.occursOn.localeCompare(b.occursOn));
  const first = ahead[0];
  return first === undefined ? null : { occursOn: first.occursOn, payee: first.payee };
}

/**
 * The focus goal (§A 8): the newest open GOAL with an envelope (its progress and target live on the
 * envelope), in scope — any of the person's for Just me, the space's own in a space.
 */
export function focusGoalOf(items: readonly MeetingItem[], scope: HomeScope): MeetingItem | null {
  const goals = items.filter(
    (item) =>
      item.kind === 'GOAL' &&
      item.status === 'OPEN' &&
      item.envelopeId !== null &&
      (scope.kind === 'me' || item.virtualAccountId === scope.virtualAccount.id),
  );
  let newest: MeetingItem | null = null;
  for (const goal of goals) {
    if (newest === null || goal.createdAt > newest.createdAt) newest = goal;
  }
  return newest;
}

const NUDGE_MEETING_MS = 36 * 60 * 60 * 1000;

export type Nudge =
  | { kind: 'meeting'; entry: MeetingCalendarEntry }
  | { kind: 'review'; count: number }
  | { kind: 'unallocated'; count: number }
  | { kind: 'calm' };

/**
 * Today's gentle nudge (§A 9), the first rule that holds:
 * 1. a meeting in scope starts within 36 hours (or is live);
 * 2. review items are waiting (Just me: the review queue is the person's own);
 * 3. lines aren't in an envelope yet (Just me: the person's own books);
 * 4. otherwise the calm line.
 *
 * A read that hasn't loaded or failed is `null` and its rule doesn't hold.
 */
export function nudgeOf(input: {
  meeting: MeetingCalendarEntry | null;
  reviewTotal: number | null;
  unallocatedLines: number | null;
  now: number;
}): Nudge {
  const { meeting, now } = input;
  if (meeting !== null) {
    const startsIn = Date.parse(meeting.startsAt) - now;
    if (meeting.live || startsIn <= NUDGE_MEETING_MS) return { kind: 'meeting', entry: meeting };
  }
  if (input.reviewTotal !== null && input.reviewTotal > 0) {
    return { kind: 'review', count: input.reviewTotal };
  }
  if (input.unallocatedLines !== null && input.unallocatedLines > 0) {
    return { kind: 'unallocated', count: input.unallocatedLines };
  }
  return { kind: 'calm' };
}

/** Whole civil days from `from` to `to` (both `YYYY-MM-DD`). */
export function civilDaysBetween(from: string, to: string): number {
  const ms = (civil: string) =>
    Date.UTC(Number(civil.slice(0, 4)), Number(civil.slice(5, 7)) - 1, Number(civil.slice(8, 10)));
  return Math.round((ms(to) - ms(from)) / 86_400_000);
}
